import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { alpha } from '@mui/material/styles';
import { Box, Stack, Typography, ListItemButton } from '@mui/material';

import Iconify from '@/components/Iconify';
import { fToNow } from '@/utils/formatTime';
import { fLinkBuild } from '@/utils/formatLink';
import { NotificationObjectProps } from '../types';
import { getNotificationUpdateRequest } from '../api';
import { getBookingSession } from '@/features/bookings-management/api';
import { bookingsManagementPath } from '@/features/bookings-management/routes/paths';
import { returnSessionFilterFromDate } from '@/features/bookings-management/utils/bookingSessions';
import {
  getNotificationCategoryMeta,
  getTaskNotificationPath,
  hasRenderableNotification,
} from '../utils';

type NotificationItemProps = {
  popover?: boolean;
  notificationObject: NotificationObjectProps;
};

export default function NotificationItem({
  notificationObject,
  popover = false,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const renderableNotificationObject = hasRenderableNotification(
    notificationObject
  )
    ? notificationObject
    : null;
  const notification = renderableNotificationObject?.notification;
  const id = renderableNotificationObject?.id;
  const seen = renderableNotificationObject?.seen;
  const recordId = notification?.record_id ?? '';
  const entityType = notification?.notification_type.entity_type;
  const dashboardRoute = entityType?.dashboard_route ?? '';
  const table = entityType?.table ?? '';

  const handleNotificationClick = useCallback(async () => {
    if (!notification || id === undefined) return;

    try {
      if (!seen) await getNotificationUpdateRequest(id);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['notifications', 'popover'],
        }),
        queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] }),
      ]);
      let targetPath = fLinkBuild(dashboardRoute, recordId);
      const taskNotificationPath = getTaskNotificationPath({
        table,
        dashboardRoute,
        recordId,
      });

      if (taskNotificationPath) {
        targetPath = taskNotificationPath;
      }

      if (table === 'booking_session') {
        const sessionId = Number(recordId);
        if (!Number.isNaN(sessionId)) {
          try {
            const session = await getBookingSession(sessionId);
            const filterCandidate = returnSessionFilterFromDate(session.date);
            const filter = ['previous', 'today', 'upcoming'].includes(
              filterCandidate
            )
              ? filterCandidate
              : 'upcoming';
            const bookingPath = bookingsManagementPath('view', {
              bookingId: String(session.booking_id),
              bookingTabSid: 'booking-sessions',
              sessionFilter: filter,
            });
            targetPath = `${bookingPath}?sessionID=${sessionId}`;
          } catch (fetchError) {
            console.error(
              'Failed to resolve booking session for notification',
              fetchError
            );
          }
        }
      }

      navigate(targetPath);
    } catch (err) {
      console.error((err as Error).message);
    }
  }, [
    notification,
    id,
    seen,
    queryClient,
    navigate,
    dashboardRoute,
    recordId,
    table,
  ]);

  if (!notification) return null;

  const category = getNotificationCategoryMeta(notification);

  return (
    <ListItemButton
      onClick={handleNotificationClick}
      sx={{
        alignItems: 'flex-start',
        gap: popover ? 1.25 : 1.75,
        py: popover ? 1.25 : 1.8,
        px: popover ? 2 : { xs: 2, sm: 2.5 },
        mt: popover ? 0 : 1,
        borderRadius: popover ? 0 : 1,
        border: popover ? 0 : '1px solid',
        borderColor: !seen ? alpha(category.color, 0.22) : 'divider',
        bgcolor: !seen ? alpha(category.color, 0.06) : 'background.paper',
        '&:hover': {
          bgcolor: alpha(category.color, !seen ? 0.1 : 0.05),
        },
      }}
    >
      <Box
        sx={{
          width: popover ? 36 : 44,
          height: popover ? 36 : 44,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: 'common.white',
          bgcolor: category.color,
          boxShadow: !popover
            ? `0 8px 18px -12px ${alpha(category.color, 0.9)}`
            : 'none',
        }}
      >
        <Iconify icon={category.icon} width={popover ? 18 : 22} />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          component="div"
          variant={popover ? 'body2' : 'subtitle2'}
          sx={{
            color: seen ? 'text.secondary' : 'text.primary',
            fontWeight: seen ? 500 : 700,
            lineHeight: 1.45,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          <Box
            component="span"
            sx={{ color: 'text.primary', fontWeight: 700, mr: 0.5 }}
          >
            {notification.title}
          </Box>
          <Box
            component="span"
            sx={{
              color: seen ? 'text.secondary' : 'text.secondary',
              fontWeight: 500,
              '& p': { m: 0, display: 'inline' },
              '& a': { color: 'inherit', textDecoration: 'none' },
              '& br': { display: 'none' },
            }}
            dangerouslySetInnerHTML={{
              __html: notification.description,
            }}
          />
        </Typography>

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ mt: 0.45, color: 'text.disabled' }}
        >
          <Iconify icon="eva:clock-outline" width={14} />
          <Typography
            variant="caption"
            sx={{
              lineHeight: 1.3,
              color: 'text.disabled',
            }}
          >
            {fToNow(notification.created_at)}
          </Typography>
        </Stack>
      </Box>

      {!seen && (
        <Box
          aria-label="Unread notification"
          sx={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            bgcolor: category.color,
            flexShrink: 0,
            mt: popover ? 1.5 : 2,
          }}
        />
      )}
    </ListItemButton>
  );
}
